import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, inboxApi, type PermissionDefinition, type Role } from '@wa/shared';

/**
 * Roles, composed from the platform's permission catalogue.
 *
 * The catalogue is fetched rather than listed here, and that is the same decision
 * the server side made: there is one definition of what a permission is, it lives
 * in the binary that enforces it, and a second copy in the portal is a copy that
 * goes stale the first time somebody adds one.
 */
export function RolesPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | 'new' | null>(null);

  const roles = useQuery({ queryKey: ['roles'], queryFn: inboxApi.roles });
  const catalogue = useQuery({
    queryKey: ['permission-catalogue'],
    queryFn: inboxApi.permissionCatalogue,
    staleTime: Infinity,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['roles'] });
    void queryClient.invalidateQueries({ queryKey: ['team'] });
  };

  const fail = (e: unknown, fallback: string) => setError(e instanceof ApiError ? e.message : fallback);

  const create = useMutation({
    mutationFn: inboxApi.createRole,
    onSuccess: () => { setError(null); setEditing(null); refresh(); },
    onError: (e) => fail(e, 'Could not create that role.'),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof inboxApi.updateRole>[1] }) =>
      inboxApi.updateRole(id, patch),
    onSuccess: () => { setError(null); setEditing(null); refresh(); },
    onError: (e) => fail(e, 'Could not change that role.'),
  });

  const remove = useMutation({
    mutationFn: inboxApi.deleteRole,
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => fail(e, 'Could not delete that role.'),
  });

  const list = roles.data ?? [];
  const permissions = catalogue.data ?? [];
  const busy = create.isPending || update.isPending || remove.isPending;

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-(--color-line) px-3 py-2.5 sm:px-4">
          <h2 className="text-body font-semibold">{list.length} roles</h2>
          <button
            type="button"
            onClick={() => setEditing(editing === 'new' ? null : 'new')}
            className="tap rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink)"
          >
            {editing === 'new' ? 'Cancel' : 'New role'}
          </button>
        </div>

        {editing === 'new' && (
          <RoleEditor
            catalogue={permissions}
            isBusy={busy}
            onSave={(draft) => create.mutate(draft)}
            onCancel={() => setEditing(null)}
          />
        )}

        <ul className="divide-y divide-(--color-line-dim)">
          {list.map((role) => (
            <li key={role.id} className="px-3 py-2.5 sm:px-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <div className="min-w-0 flex-1 basis-48">
                  <p className="flex items-center gap-1.5 truncate text-small font-medium">
                    <span className="truncate">{role.name}</span>
                    {role.isSystem && (
                      <span className="shrink-0 rounded-(--radius-chip) bg-(--color-sunken) px-1.5 py-0.5 text-micro font-normal text-(--color-muted)">
                        built in
                      </span>
                    )}
                  </p>
                  <p className="truncate text-small text-(--color-muted)">
                    {role.permissions.length} permissions &middot; {role.agentCount}{' '}
                    {role.agentCount === 1 ? 'person' : 'people'}
                    {role.description && <> &middot; {role.description}</>}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(editing === role.id ? null : role.id)}
                    aria-expanded={editing === role.id}
                    className="tap rounded border border-(--color-line) px-2 py-1 text-small text-(--color-muted) hover:bg-(--color-sunken)"
                  >
                    {editing === role.id ? 'Close' : 'Edit'}
                  </button>

                  {/* Built-in roles are what new agents land on and what the account
                      is recovered through. Their permissions can still be changed. */}
                  {!role.isSystem && (
                    <button
                      type="button"
                      disabled={busy || role.agentCount > 0}
                      title={role.agentCount > 0 ? 'Move these people to another role first.' : undefined}
                      onClick={() => remove.mutate(role.id)}
                      className="tap rounded border border-(--color-line) px-2 py-1 text-small text-(--color-muted) transition hover:bg-(--color-sunken) hover:text-(--color-bad) disabled:opacity-40"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

              {editing === role.id && (
                <RoleEditor
                  role={role}
                  catalogue={permissions}
                  isBusy={busy}
                  onSave={(draft) => update.mutate({ id: role.id, patch: draft })}
                  onCancel={() => setEditing(null)}
                />
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function RoleEditor({ role, catalogue, isBusy, onSave, onCancel }: {
  role?: Role;
  catalogue: PermissionDefinition[];
  isBusy: boolean;
  onSave: (draft: { name: string; description?: string; permissions: string[] }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [chosen, setChosen] = useState<string[]>(role?.permissions ?? []);

  const groups = [...new Set(catalogue.map((p) => p.group))];
  const ready = role?.isSystem === true || name.trim().length > 0;

  const toggle = (key: string) =>
    setChosen((set) => (set.includes(key) ? set.filter((k) => k !== key) : [...set, key]));

  return (
    <div className="mt-3 flex flex-col gap-3 rounded-(--radius-field) bg-(--color-sunken) p-3">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-36 flex-1 flex-col gap-1">
          <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">Name</span>
          <input
            value={name}
            disabled={role?.isSystem === true}
            onChange={(e) => setName(e.target.value)}
            placeholder="Marketing"
            title={role?.isSystem === true ? 'A built-in role cannot be renamed.' : undefined}
            className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small disabled:opacity-60"
          />
        </label>

        <label className="flex min-w-48 flex-2 flex-col gap-1">
          <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">Description</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this role is for"
            className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
          />
        </label>
      </div>

      {/* Grouped by what the person is doing rather than listed flat. Eighteen
          checkboxes in one column is a form nobody reads to the bottom of. */}
      <div className="grid gap-3 @min-[34rem]:grid-cols-2">
        {groups.map((group) => (
          <fieldset key={group} className="rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) p-2.5">
            <legend className="px-1 text-micro font-medium uppercase tracking-wide text-(--color-faint)">
              {group}
            </legend>

            <div className="flex flex-col gap-1.5">
              {catalogue.filter((p) => p.group === group).map((permission) => (
                <label key={permission.key} className="flex items-start gap-2 text-small">
                  <input
                    type="checkbox"
                    checked={chosen.includes(permission.key)}
                    onChange={() => toggle(permission.key)}
                    className="mt-0.5 shrink-0"
                  />
                  <span className="min-w-0">
                    <span className="block">{permission.label}</span>
                    <span className="block text-micro leading-snug text-(--color-muted)">{permission.detail}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!ready || isBusy}
          onClick={() => onSave({
            name: name.trim(),
            description: description.trim() || undefined,
            permissions: chosen,
          })}
          className="tap rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
        >
          {role ? 'Save role' : 'Create role'}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="tap rounded border border-(--color-line) px-3 py-1.5 text-small text-(--color-muted) hover:bg-(--color-surface)"
        >
          Cancel
        </button>

        {role && role.agentCount > 0 && (
          <p className="text-micro text-(--color-muted)">
            {role.agentCount} {role.agentCount === 1 ? 'person is' : 'people are'} on this role and will be
            signed out when it changes.
          </p>
        )}
      </div>
    </div>
  );
}
